import { useState, useEffect } from 'react'
import { Box, VStack, HStack, Heading, Button, IconButton } from '@chakra-ui/react';
import { FiPlus, FiTrash2 } from 'react-icons/fi'
import { FilterRow } from './FilterRow'
import type { FilterGroup, FilterRow as FilterRowType, FilterFieldMetadata, TagOption, FilterOperator } from '../../types/contactList'
import { uuid } from '../../lib/uuid'

interface FilterGroupCardProps {
  group: FilterGroup
  fields: FilterFieldMetadata[]
  tags: TagOption[]
  onChange: (updated: FilterGroup) => void
  onRemove: () => void
  canRemove: boolean
}

export function FilterGroupCard({
  group,
  fields,
  tags,
  onChange,
  onRemove,
  canRemove,
}: FilterGroupCardProps) {
  const categories = [...new Set(fields.map(f => f.category))]

  // Track selected category per row. Default to first category.
  const [rowCategories, setRowCategories] = useState<Record<string, string>>(() => {
    const map: Record<string, string> = {}
    for (const filter of group.filters) {
      const field = fields.find(f => f.fieldName === filter.fieldName)
      map[filter.id] = field?.category ?? categories[0] ?? ''
    }
    return map
  })

  // Re-derive categories when field metadata loads (fixes stale '' values from initial render)
  useEffect(() => {
    if (fields.length === 0) return
    const validCats = new Set(fields.map(f => f.category))
    setRowCategories(prev => {
      const next = { ...prev }
      let changed = false
      for (const filter of group.filters) {
        const current = prev[filter.id]
        if (!current || !validCats.has(current)) {
          const field = fields.find(f => f.fieldName === filter.fieldName)
          const derived = field?.category ?? categories[0] ?? ''
          if (derived !== current) {
            next[filter.id] = derived
            changed = true
          }
        }
      }
      return changed ? next : prev
    })
  }, [fields, group.filters, categories])

  const handleFilterChange = (index: number, updated: FilterRowType) => {
    const newFilters = [...group.filters]
    newFilters[index] = updated
    onChange({ ...group, filters: newFilters })
  }

  const handleCategoryChange = (filterId: string, category: string) => {
    setRowCategories(prev => ({ ...prev, [filterId]: category }))
  }

  const handleRemoveFilter = (index: number) => {
    const newFilters = group.filters.filter((_, i) => i !== index)
    onChange({ ...group, filters: newFilters })
  }

  const handleAddFilter = () => {
    const id = uuid()
    const defaultCategory = categories[0] ?? ''
    const defaultField = fields.find(f => f.category === defaultCategory)
    const defaultOperator: FilterOperator = 'Equals'
    const newFilter: FilterRowType = {
      id,
      fieldName: defaultField?.fieldName ?? '',
      operator: defaultOperator,
      value: undefined,
      displayOrder: group.filters.length,
    }
    setRowCategories(prev => ({ ...prev, [id]: defaultCategory }))
    onChange({ ...group, filters: [...group.filters, newFilter] })
  }

  return (
    <Box bg="surface" p={{ base: 4, md: 6 }} borderRadius="lg" shadow="md" borderWidth="1px" borderColor="gray.200">
      <HStack justify="space-between" mb={4}>
        <Heading size="sm" color="gray.600">
          Group {group.groupIndex + 1}
        </Heading>
        {canRemove && (
          <IconButton
            aria-label="Remove group"
            size="sm"
            variant="ghost"
            colorPalette="red"
            onClick={onRemove}><FiTrash2 /></IconButton>
        )}
      </HStack>

      <VStack gap={3} align="stretch">
        {group.filters.map((filter, index) => (
          <FilterRow
            key={filter.id}
            filter={filter}
            fields={fields}
            tags={tags}
            categories={categories}
            selectedCategory={rowCategories[filter.id] ?? categories[0] ?? ''}
            onCategoryChange={(cat) => handleCategoryChange(filter.id, cat)}
            onChange={(updated) => handleFilterChange(index, updated)}
            onRemove={() => handleRemoveFilter(index)}
            showAndLabel={index > 0}
          />
        ))}

        <Button
          variant="ghost"
          size="sm"
          colorPalette="brand"
          onClick={handleAddFilter}
          w={{ base: 'full', md: 'auto' }}
          minH="44px"
          alignSelf={{ base: 'stretch', md: 'flex-start' }}><FiPlus />Add condition
                  </Button>
      </VStack>
    </Box>
  );
}
